import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import { GitCompare, Brain, Calendar, HelpCircle } from "lucide-react";

export default function Bipolar1Vs2DifferencePage() {
  const publishedDate = "2024-08-18";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/08/what-is-the-difference-between-bipolar-1-and-bipolar-2.jpg";

  const faqs = [
    {
      q: "What is the main difference between Bipolar 1 and Bipolar 2?",
      a: "Bipolar 1 requires at least one full manic episode (potentially involving psychosis or hospitalization). Bipolar 2 requires at least one hypomanic episode (milder, shorter elevated mood) plus at least one major depressive episode—but never a full manic episode.",
    },
    {
      q: "Can Bipolar 2 turn into Bipolar 1?",
      a: "Yes, though uncommon. Approximately 5–15% of individuals with Bipolar 2 eventually experience a full manic episode, which would reclassify their diagnosis as Bipolar 1. This is more likely with antidepressant-triggered manic switches.",
    },
    {
      q: "Which is harder to live with—Bipolar 1 or Bipolar 2?",
      a: "Both present significant challenges. Bipolar 1 involves more severe manic episodes, while Bipolar 2 often involves longer, more frequent depressive episodes with significant functional impairment. Both require expert psychiatric management.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="What is the Difference Between Bipolar 1 and Bipolar 2? | Neuro Wellness"
        description="Understand the key differences between Bipolar 1 and Bipolar 2 disorder—mania vs hypomania, depressive burden, diagnostic criteria, and treatment approaches."
        canonical="/what-is-the-difference-between-bipolar-1-and-bipolar-2"
        keywords="difference between bipolar 1 and bipolar 2, mania vs hypomania, bipolar spectrum diagnosis, bipolar disorder Coral Springs FL"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/what-is-the-difference-between-bipolar-1-and-bipolar-2#webpage",
                name: "What is the Difference Between Bipolar 1 and Bipolar 2?",
                url: "https://neurowellnesstms.com/what-is-the-difference-between-bipolar-1-and-bipolar-2",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Clinical comparison of Bipolar 1 and Bipolar 2 including manic vs hypomanic episodes, diagnostic criteria, and treatment differences.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/what-is-the-difference-between-bipolar-1-and-bipolar-2#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <GitCompare className="h-4 w-4" />
                    Diagnostic Comparison
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Aug 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  What is the{" "}
                  <span className="text-accent">
                    Difference Between Bipolar 1 and Bipolar 2?
                  </span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Mania vs hypomania, depressive burden, and the diagnostic
                  criteria that distinguish these two mood disorders.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Diagnostic Consult
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Get Bipolar Care Guidance
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "Bipolar 1 vs Bipolar 2",
                  href: "/what-is-the-difference-between-bipolar-1-and-bipolar-2",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                Bipolar disorder is a spectrum condition, and the distinction
                between Bipolar 1 and Bipolar 2 is one of the most clinically
                important—and commonly misunderstood—diagnostic differentiations
                in psychiatry.
              </p>

              <h2>Bipolar 1: Defined by Mania</h2>
              <p>
                <strong>Bipolar 1 Disorder</strong> is diagnosed when a person
                experiences at least one <strong>manic episode</strong>—a period
                of abnormally elevated, expansive, or irritable mood lasting at
                least 7 days (or requiring hospitalization). Mania may include
                psychosis, grandiosity, severely decreased need for sleep,
                racing thoughts, and risky behavior. Depressive episodes
                commonly occur but are not required for diagnosis.
              </p>

              <h2>Bipolar 2: Defined by Hypomania & Depression</h2>
              <p>
                <strong>Bipolar 2 Disorder</strong> requires at least one{" "}
                <strong>hypomanic episode</strong> (a milder form of mania
                lasting at least 4 days, without psychosis or significant
                functional impairment) and at least one{" "}
                <strong>major depressive episode</strong>. Crucially, a full
                manic episode has <em>never</em> occurred—if it does, the
                diagnosis changes to Bipolar 1.
              </p>

              <h2>Key Differences at a Glance</h2>
              <ul>
                <li>
                  <strong>Mania (Bipolar 1):</strong> Severe, may require
                  hospitalization, may involve psychosis, severe functional
                  impairment.
                </li>
                <li>
                  <strong>Hypomania (Bipolar 2):</strong> Milder, observable
                  change in mood, no psychosis, less functional impairment.
                </li>
                <li>
                  <strong>Depressive Burden:</strong> Bipolar 2 patients often
                  spend more time in depression than Bipolar 1 patients.
                </li>
                <li>
                  <strong>Misdiagnosis Risk:</strong> Bipolar 2 is frequently
                  misdiagnosed as unipolar depression because hypomania can feel
                  productive.
                </li>
              </ul>

              <h2>Treatment Implications</h2>
              <p>
                Both types require mood stabilizers, but the approach differs.
                Antidepressants alone can trigger manic switching in both types.
                For bipolar depression that resists standard treatment,{" "}
                <Link
                  to="/how-does-bipolar-disorder-affect-the-brain"
                  className="text-primary hover:underline font-semibold"
                >
                  understanding the neurobiology
                </Link>{" "}
                guides advanced options like{" "}
                <Link
                  to="/what-is-tms-therapy"
                  className="text-primary hover:underline font-semibold"
                >
                  TMS therapy
                </Link>{" "}
                carefully monitored by psychiatrists.
              </p>
            </article>

            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related Bipolar Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link
                    to="/how-does-bipolar-disorder-affect-the-brain"
                    className="hover:underline"
                  >
                    → How Bipolar Affects the Brain
                  </Link>
                </li>
                <li>
                  <Link
                    to="/essential-facts-bipolar-1-vs-2-coral-springs"
                    className="hover:underline"
                  >
                    → Bipolar 1 vs 2 Coral Springs Essentials
                  </Link>
                </li>
                <li>
                  <Link
                    to="/what-is-the-difference-between-anxiety-and-depression"
                    className="hover:underline"
                  >
                    → Anxiety vs Depression
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-to-see-a-psychiatrist"
                    className="hover:underline"
                  >
                    → How to See a Psychiatrist
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
